import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../services/api'
import { twoFactorLoginApi } from '../../services/api'
import {
  ShieldCheck,
  Smartphone,
  Mail,
  Key,
  Fingerprint,
  Loader2,
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Monitor,
  ChevronDown,
  BookOpen
} from 'lucide-react'

// Chiave localStorage per device token
const DEVICE_TOKEN_KEY = 'diario_trusted_device'

// Helper: converte ArrayBuffer in base64url
function arrayBufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

// Helper: converte base64url in Uint8Array
function base64urlToUint8Array(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padding = '='.repeat((4 - base64.length % 4) % 4)
  const binary = atob(base64 + padding)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

// Helper: verifica supporto WebAuthn
function isWebAuthnSupported() {
  return !!(
    window.PublicKeyCredential &&
    typeof window.PublicKeyCredential === 'function'
  )
}

export default function TwoFactorVerifyPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { completeLogin } = useAuth()
  
  // Dati passati dalla pagina login
  const twoFactorData = location.state?.twoFactorData
  
  // =========================================
  // FIX: Usa twoFactorToken (coerente con backend)
  // =========================================
  const twoFactorToken = twoFactorData?.twoFactorToken
  
  // FIX: Ref per evitare loop - traccia se il check è già stato fatto
  const checkDoneRef = useRef(false)
  const loginCompletedRef = useRef(false)
  
  // Converti methods object in array di metodi disponibili
  const getAvailableMethods = () => {
    if (!twoFactorData?.methods) return []
    const methods = twoFactorData.methods
    const available = []
    if (methods.totp) available.push('totp')
    if (methods.email) available.push('email')
    if (methods.webauthn && isWebAuthnSupported()) available.push('webauthn')
    // Aggiungi sempre backup come opzione se almeno un metodo è disponibile
    if (available.length > 0) available.push('backup')
    return available
  }
  
  // Determina metodo preferito (primo disponibile)
  const getPreferredMethod = () => {
    const methods = twoFactorData?.methods
    if (!methods) return 'totp'
    // Preferisci WebAuthn se disponibile (più veloce)
    if (methods.webauthn && isWebAuthnSupported()) return 'webauthn'
    if (methods.totp) return 'totp'
    if (methods.email) return 'email'
    return 'totp'
  }
  // =========================================
  
  const [loading, setLoading] = useState(false)
  const [checkingDevice, setCheckingDevice] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  
  // Metodo selezionato
  const [selectedMethod, setSelectedMethod] = useState(null)
  
  // Codice inserito
  const [code, setCode] = useState('')
  const inputRefs = useRef([])
  
  // Email OTP
  const [emailSent, setEmailSent] = useState(false)
  const [emailCooldown, setEmailCooldown] = useState(0)
  const [sendingEmail, setSendingEmail] = useState(false)

  // FASE 5: Remember device
  // FIX: Leggi rememberDevice dallo state passato dalla LoginPage
  const [rememberDevice, setRememberDevice] = useState(location.state?.rememberDevice || false)
  const [canRemember, setCanRemember] = useState(location.state?.twoFactorData?.canRememberDevice || false)

  // WebAuthn state
  const [webAuthnLoading, setWebAuthnLoading] = useState(false)
  const [qrGuideOpen, setQrGuideOpen] = useState(false)

  // Check se dispositivo è già trusted
  useEffect(() => {
    const checkTrustedDevice = async () => {
      // FIX: Evita loop - esegui solo una volta
      if (checkDoneRef.current || loginCompletedRef.current) {
        return
      }
      
      // FIX: Usa twoFactorToken
      if (!twoFactorToken) {
        console.log('No twoFactorToken found, redirecting to login')
        navigate('/login', { replace: true })
        return
      }
      
      // Marca come fatto PRIMA della chiamata async
      checkDoneRef.current = true

      try {
        // Recupera device token da localStorage
        const storedDeviceToken = localStorage.getItem(DEVICE_TOKEN_KEY)
        
        if (storedDeviceToken) {
          // FIX: Usa endpoint /auth/2fa-login/check-trusted
          const response = await api.post('/auth/2fa-login/check-trusted', {
            twoFactorToken: twoFactorToken,
            deviceToken: storedDeviceToken
          })

          if (response.data.success && response.data.data.trusted) {
            // Dispositivo trusted - login automatico!
            loginCompletedRef.current = true
            setSuccess(true)
            
            // Cookie httpOnly impostati dal backend
            const data = response.data.data

            await completeLogin(data)
            
            // FIX: Usa window.location per forzare refresh completo
            window.location.href = '/dashboard'
            return
          }

          // Token non valido - rimuovilo
          if (!response.data.data.trusted) {
            localStorage.removeItem(DEVICE_TOKEN_KEY)
          }

          // Imposta se può usare remember device
          setCanRemember(response.data.data.canRemember || twoFactorData?.canRememberDevice || false)
        } else {
          // Nessun token - verifica se può usare remember
          try {
            // FIX: Usa endpoint /auth/2fa-login/check-trusted
            const response = await api.post('/auth/2fa-login/check-trusted', {
              twoFactorToken: twoFactorToken,
              deviceToken: null
            })
            setCanRemember(response.data.data?.canRemember || twoFactorData?.canRememberDevice || false)
          } catch (err) {
            // Se l'endpoint non esiste, usa il valore dal twoFactorData
            setCanRemember(twoFactorData?.canRememberDevice || false)
          }
        }

        // Imposta metodo preferito
        setSelectedMethod(getPreferredMethod())

      } catch (err) {
        console.error('Check trusted device error:', err)
        if (err.response?.data?.code === 'SESSION_EXPIRED') {
          navigate('/login', { replace: true })
          return
        }
        // Continua normalmente se errore
        setCanRemember(twoFactorData?.canRememberDevice || false)
        setSelectedMethod(getPreferredMethod())
      } finally {
        setCheckingDevice(false)
      }
    }

    checkTrustedDevice()
  // FIX: Rimosse dipendenze che causano loop (completeLogin cambia ad ogni render)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twoFactorToken, navigate])

  // Countdown per email cooldown
  useEffect(() => {
    if (emailCooldown > 0) {
      const timer = setTimeout(() => setEmailCooldown(emailCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [emailCooldown])

  // Auto-focus sul primo input
  useEffect(() => {
    if (!checkingDevice && inputRefs.current[0] && selectedMethod !== 'webauthn') {
      inputRefs.current[0].focus()
    }
  }, [selectedMethod, checkingDevice])

  // Gestione input codice (6 cifre separate)
  const handleCodeChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    
    const newCode = code.split('')
    newCode[index] = digit
    const updatedCode = newCode.join('').slice(0, 6)
    setCode(updatedCode)
    
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
    
    if (updatedCode.length === 6) {
      handleVerify(updatedCode)
    }
  }

  // Gestione backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  // Gestione paste
  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    setCode(pastedData)
    
    if (pastedData.length === 6) {
      handleVerify(pastedData)
    }
  }

  // Invia codice email
  const handleSendEmailCode = async () => {
    try {
      setSendingEmail(true)
      setError(null)
      
      // FIX: Usa endpoint /auth/2fa-login/send-code
      await api.post('/auth/2fa-login/send-code', {
        twoFactorToken: twoFactorToken
      })
      
      setEmailSent(true)
      setEmailCooldown(60)
      
    } catch (err) {
      console.error('Send email code error:', err)
      if (err.response?.data?.code === 'SESSION_EXPIRED') {
        setError('Sessione scaduta. Effettua nuovamente il login.')
        setTimeout(() => navigate('/login'), 2000)
      } else if (err.response?.data?.waitSeconds) {
        setEmailCooldown(err.response.data.waitSeconds)
      } else {
        setError(err.response?.data?.message || 'Errore nell\'invio del codice')
      }
    } finally {
      setSendingEmail(false)
    }
  }

  // Verifica codice (TOTP, Email, Backup)
  const handleVerify = async (codeToVerify = code) => {
    // FIX: Evita doppia chiamata
    if (loading || loginCompletedRef.current) return
    
    if (codeToVerify.length < 6 && selectedMethod !== 'backup') {
      setError('Inserisci il codice completo')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      // FIX: Usa endpoint /auth/2fa-login/verify
      const response = await api.post('/auth/2fa-login/verify', {
        twoFactorToken: twoFactorToken,
        code: codeToVerify,
        method: selectedMethod,
        rememberDevice: rememberDevice && canRemember // FASE 5
      })
      
      if (response.data.success) {
        // FIX: Marca come completato PRIMA di tutto
        loginCompletedRef.current = true
        setSuccess(true)
        
        // FASE 5: Salva device token se fornito (non è un JWT, è un device identifier)
        const { data } = response.data
        if (data.deviceToken) {
          localStorage.setItem(DEVICE_TOKEN_KEY, data.deviceToken)
        }

        // Cookie httpOnly impostati dal backend — aggiorna solo lo state React
        await completeLogin(data)

        // Redirect con refresh completo
        window.location.href = '/dashboard'
      }
      
    } catch (err) {
      console.error('2FA verify error:', err)
      setCode('')
      
      if (err.response?.data?.code === 'SESSION_EXPIRED') {
        setError('Sessione scaduta. Effettua nuovamente il login.')
        setTimeout(() => navigate('/login'), 2000)
      } else if (err.response?.data?.code === 'TOO_MANY_ATTEMPTS' || err.response?.data?.code === 'RATE_LIMITED') {
        setError(err.response?.data?.message || 'Troppi tentativi. Riprova più tardi.')
        if (err.response?.data?.waitSeconds > 60) {
          setTimeout(() => navigate('/login'), 3000)
        }
      } else {
        setError(err.response?.data?.message || 'Codice non valido')
      }
      
      inputRefs.current[0]?.focus()
    } finally {
      setLoading(false)
    }
  }

  // WebAuthn login
  const handleWebAuthnLogin = async () => {
    if (webAuthnLoading || loginCompletedRef.current) return

    try {
      setWebAuthnLoading(true)
      setError(null)

      // 1. Ottieni opzioni dal server
      const optionsResponse = await twoFactorLoginApi.webauthnAuthOptions(twoFactorToken)

      if (!optionsResponse.data.success) {
        throw new Error(optionsResponse.data.message || 'Errore ottenimento opzioni')
      }

      const options = optionsResponse.data.data

      // 2. Converti challenge e allowCredentials da base64url a Uint8Array
      const publicKeyOptions = {
        ...options,
        challenge: base64urlToUint8Array(options.challenge)
      }

      if (options.allowCredentials) {
        publicKeyOptions.allowCredentials = options.allowCredentials.map(cred => ({
          ...cred,
          id: base64urlToUint8Array(cred.id)
        }))
      }

      // 3. Chiedi al browser di verificare la credenziale (mostra prompt biometrico)
      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions
      })

      if (!credential) {
        throw new Error('Autenticazione passkey annullata')
      }

      // 4. Prepara risposta per il server
      const authenticationResponse = {
        id: credential.id,
        rawId: arrayBufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON),
          authenticatorData: arrayBufferToBase64url(credential.response.authenticatorData),
          signature: arrayBufferToBase64url(credential.response.signature),
          userHandle: credential.response.userHandle 
            ? arrayBufferToBase64url(credential.response.userHandle) 
            : null
        }
      }

      // 5. Verifica sul server
      const verifyResponse = await twoFactorLoginApi.webauthnVerify(
        twoFactorToken,
        authenticationResponse,
        rememberDevice && canRemember
      )

      if (verifyResponse.data.success) {
        loginCompletedRef.current = true
        setSuccess(true)

        const { data } = verifyResponse.data

        // Salva device token se fornito (non è un JWT, è un device identifier)
        if (data.deviceToken) {
          localStorage.setItem(DEVICE_TOKEN_KEY, data.deviceToken)
        }

        // Cookie httpOnly impostati dal backend — aggiorna solo lo state React
        await completeLogin(data)
        window.location.href = '/dashboard'
      } else {
        throw new Error(verifyResponse.data.message || 'Verifica passkey fallita')
      }

    } catch (err) {
      console.error('WebAuthn login error:', err)

      // Gestisci errori specifici WebAuthn
      if (err.name === 'NotAllowedError') {
        setError('Autenticazione annullata o timeout. Riprova.')
      } else if (err.name === 'SecurityError') {
        setError('Errore di sicurezza. Verifica di essere su una connessione sicura.')
      } else if (err.response?.data?.code === 'SESSION_EXPIRED') {
        setError('Sessione scaduta. Effettua nuovamente il login.')
        setTimeout(() => navigate('/login'), 2000)
      } else {
        setError(err.response?.data?.message || err.message || 'Errore durante l\'autenticazione con passkey')
      }
    } finally {
      setWebAuthnLoading(false)
    }
  }

  // Torna al login
  const handleBack = () => {
    navigate('/login', { replace: true })
  }

  // Cambia metodo
  const handleMethodChange = (method) => {
    setSelectedMethod(method)
    setCode('')
    setError(null)
    setEmailSent(false)
  }

  // Loading iniziale - check dispositivo
  if (checkingDevice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Verifica in corso...</p>
        </div>
      </div>
    )
  }

  if (!twoFactorData || !twoFactorToken) {
    return null
  }

  const availableMethods = getAvailableMethods()

  const methodIcons = {
    totp: Smartphone,
    email: Mail,
    webauthn: Fingerprint,
    backup: Key
  }

  const methodLabels = {
    totp: 'Google Authenticator',
    email: 'Email',
    webauthn: 'Passkey',
    backup: 'Codice di backup'
  }

  // Email mascherata per mostrare all'utente
  const maskedEmail = twoFactorData.email || twoFactorData.user?.email || ''

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Verifica in due passaggi
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              {twoFactorData.user?.nome 
                ? `Ciao ${twoFactorData.user.nome}, inserisci il codice di verifica`
                : 'Inserisci il codice di verifica'
              }
            </p>
          </div>

          {/* Selezione metodo (se più di uno) */}
          {availableMethods.length > 1 && (
            <div className="flex flex-wrap gap-2 justify-center mb-6">
              {availableMethods.map(method => {
                const Icon = methodIcons[method] || Key
                const isActive = selectedMethod === method
                
                return (
                  <button
                    key={method}
                    onClick={() => handleMethodChange(method)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {methodLabels[method]}
                  </button>
                )
              })}
            </div>
          )}

          {/* Contenuto in base al metodo */}
          <div className="space-y-6">
            {/* TOTP / Email / Backup - Input codice */}
            {selectedMethod !== 'webauthn' && (
              <>
                {/* Istruzioni specifiche per metodo */}
                <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                  {selectedMethod === 'totp' && (
                    <p>Inserisci il codice a 6 cifre dalla tua app authenticator</p>
                  )}
                  {selectedMethod === 'email' && !emailSent && (
                    <div className="space-y-3">
                      <p>Riceverai un codice all'indirizzo email associato al tuo account</p>
                      <button
                        onClick={handleSendEmailCode}
                        disabled={sendingEmail || emailCooldown > 0}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sendingEmail ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Invio in corso...
                          </>
                        ) : emailCooldown > 0 ? (
                          <>
                            <RefreshCw className="h-4 w-4" />
                            Riprova tra {emailCooldown}s
                          </>
                        ) : (
                          <>
                            <Mail className="h-4 w-4" />
                            Invia codice
                          </>
                        )}
                      </button>
                    </div>
                  )}
                  {selectedMethod === 'email' && emailSent && (
                    <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
                      <CheckCircle className="h-4 w-4" />
                      <p>Codice inviato! Controlla la tua email</p>
                    </div>
                  )}
                  {selectedMethod === 'backup' && (
                    <p>Inserisci uno dei tuoi codici di backup (formato: XXXX-XXXX)</p>
                  )}
                </div>

                {/* Input codice */}
                {(selectedMethod !== 'email' || emailSent) && (
                  <div className="flex justify-center gap-2" onPaste={handlePaste}>
                    {selectedMethod === 'backup' ? (
                      <input
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder="XXXX-XXXX"
                        maxLength={9}
                        className="w-40 px-4 py-3 text-center text-xl font-mono tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    ) : (
                      [...Array(6)].map((_, index) => (
                        <input
                          key={index}
                          ref={el => inputRefs.current[index] = el}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={code[index] || ''}
                          onChange={(e) => handleCodeChange(index, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          disabled={loading || success}
                          className="w-12 h-14 text-center text-2xl font-bold border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                        />
                      ))
                    )}
                  </div>
                )}

                {/* FASE 5: Checkbox remember device (solo educatori) */}
                {canRemember && (selectedMethod !== 'email' || emailSent) && (
                  <div className="flex items-center justify-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={rememberDevice}
                        onChange={(e) => setRememberDevice(e.target.checked)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200 flex items-center gap-1">
                        <Monitor className="h-4 w-4" />
                        Ricorda questo dispositivo per 14 giorni
                      </span>
                    </label>
                  </div>
                )}

                {/* Pulsante verifica per backup code */}
                {selectedMethod === 'backup' && code.length >= 8 && (
                  <button
                    onClick={() => handleVerify()}
                    disabled={loading}
                    className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Verifica in corso...
                      </>
                    ) : (
                      'Verifica'
                    )}
                  </button>
                )}

                {/* Reinvia email */}
                {selectedMethod === 'email' && emailSent && emailCooldown === 0 && (
                  <button
                    onClick={handleSendEmailCode}
                    disabled={sendingEmail}
                    className="w-full text-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    Non hai ricevuto il codice? Invia di nuovo
                  </button>
                )}
              </>
            )}

            {/* WebAuthn */}
            {selectedMethod === 'webauthn' && (
              <div className="text-center space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  Usa la tua passkey per autenticarti
                </p>
                <button
                  onClick={handleWebAuthnLogin}
                  disabled={webAuthnLoading || success}
                  className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {webAuthnLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Verifica in corso...
                    </>
                  ) : (
                    <>
                      <Fingerprint className="h-5 w-5" />
                      Usa Passkey
                    </>
                  )}
                </button>

                {/* FASE 5: Checkbox remember device (anche per WebAuthn) */}
                {canRemember && (
                  <div className="flex items-center justify-center gap-3 mt-4">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={rememberDevice}
                        onChange={(e) => setRememberDevice(e.target.checked)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200 flex items-center gap-1">
                        <Monitor className="h-4 w-4" />
                        Ricorda questo dispositivo per 14 giorni
                      </span>
                    </label>
                  </div>
                )}

                {/* Guida QR code per accesso cross-device */}
                <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setQrGuideOpen(!qrGuideOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      Come usare il codice QR
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${qrGuideOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {qrGuideOpen && (
                    <div className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 space-y-3 border-t border-gray-200 dark:border-gray-700">
                      <p>
                        Se stai accedendo da un computer o dispositivo diverso da quello in cui hai registrato la passkey, segui questi passaggi:
                      </p>
                      <ol className="list-decimal list-inside space-y-2">
                        <li>
                          Assicurati che il <strong className="text-gray-800 dark:text-gray-200">Bluetooth sia attivo</strong> sia sul computer che sullo smartphone dove hai salvato la passkey, e che i due dispositivi siano <strong className="text-gray-800 dark:text-gray-200">accoppiati tramite Bluetooth</strong>
                        </li>
                        <li>
                          I due dispositivi devono trovarsi <strong className="text-gray-800 dark:text-gray-200">vicini tra loro</strong>
                        </li>
                        <li>
                          <strong className="text-gray-800 dark:text-gray-200">Inquadra il codice QR</strong> con il tuo smartphone:
                          <ul className="mt-1 ml-5 space-y-1">
                            <li>
                              <strong className="text-gray-800 dark:text-gray-200">iPhone</strong> (iOS 16+): apri la <strong className="text-gray-800 dark:text-gray-200">Fotocamera</strong> e inquadra il QR. Il sistema ti chiederà di autenticarti con Face ID o impronta
                            </li>
                            <li>
                              <strong className="text-gray-800 dark:text-gray-200">Android</strong>: apri <strong className="text-gray-800 dark:text-gray-200">Google Lens</strong> (dall'app Google o dalla fotocamera) e inquadra il QR. Il sistema ti chiederà di autenticarti con impronta, PIN, face unlock o sequenza
                            </li>
                          </ul>
                        </li>
                        <li>
                          Dopo l'autenticazione sullo smartphone, l'accesso verrà confermato automaticamente sul computer
                        </li>
                      </ol>
                      <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                        <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          La fotocamera standard di Android potrebbe non riconoscere il codice QR. In quel caso, utilizza <strong>Google Lens</strong>. Su iPhone la fotocamera nativa funziona direttamente.
                        </p>
                      </div>
                      <p className="pt-1">
                        Non riesci ad autenticarti?{' '}
                        <button
                          type="button"
                          onClick={() => {
                            setQrGuideOpen(false)
                            const fallbackMethod = availableMethods.find(m => m !== 'webauthn' && m !== 'backup') || 'backup'
                            handleMethodChange(fallbackMethod)
                          }}
                          className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                        >
                          Usa un altro metodo di verifica
                        </button>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Errore */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
                <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Successo */}
            {success && (
              <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
                <CheckCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">Verifica completata! Reindirizzamento...</span>
              </div>
            )}

            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <ArrowLeft className="h-4 w-4" />
              Torna al login
            </button>
          </div>
        </div>

        {/* Help text */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
          Hai problemi con la verifica?{' '}
          <a href="mailto:support@ildiariodibordo.it" className="text-primary-600 hover:underline">
            Contatta il supporto
          </a>
        </p>
      </div>
    </div>
  )
}
